import { AutopilotTaskDto, TaskBlockerBlockerType } from '@urbanx/agx.activities.client';
import {
  StaffGetContractSettlementResponse,
  StaffGetContractSettlementResponseSettlementType as SettlementType,
  useStaffGetContractSettlement,
} from '@urbanx/agx.campaigns.client';
import {
  AgxEmptyState,
  AgxSpacer,
  AgxTypography,
  Icon_CheckmarkCircleOutline,
  Icon_Clock,
  toDateMediumString,
} from '@urbanx/agx.ui-components2';
import { JSX, ReactNode, useState } from 'react';
import { BlockerLayout } from './BlockerLayout';
import MarkedAsSettled from './BlockerModals/MarkAsSettled';

interface SettlementPendingBlockerProps {
  task: AutopilotTaskDto;
  children: ReactNode;
  onTasksInvalidated?: () => void | Promise<void>;
}

const SettlementStatusRow = ({
  id,
  label,
  isComplete,
}: {
  id: string;
  label: string;
  isComplete: boolean;
}): JSX.Element => (
  <AgxSpacer direction="horizontal" contentAlignment="center" gap="spacing-sm">
    {isComplete ? (
      <Icon_CheckmarkCircleOutline width={16} height={16} color="var(--fg-success)" />
    ) : (
      <Icon_Clock width={16} height={16} color="var(--text-tertiary)" />
    )}
    <AgxTypography
      id={id}
      type="body_ui"
      size="small"
      color={isComplete ? 'var(--text-success)' : 'var(--text-tertiary)'}
    >
      {label}
    </AgxTypography>
  </AgxSpacer>
);

const SettlementDescription = ({
  settlement,
}: {
  settlement?: StaffGetContractSettlementResponse;
}): JSX.Element => {
  const settlementDate = toDateMediumString(settlement?.actualSettlementDate);
  const settlementTypeLabel =
    settlement?.settlementType === SettlementType.Pexa ? 'PEXA' : 'In person';
  const hasPexaSettlementConfirmation = (settlement?.pexaSettlementConfirmations?.length ?? 0) > 0;
  const confirmationComplete =
    settlement?.settlementType === SettlementType.Pexa
      ? hasPexaSettlementConfirmation
      : settlement?.settlementType === SettlementType.InPerson &&
        !!settlement?.vendorSettlementConfirmation &&
        !!settlement?.buyerSettlementConfirmation;

  return (
    <AgxSpacer direction="vertical" contentAlignment="center" gap="spacing-ml">
      <SettlementStatusRow
        id="settlementDate"
        isComplete={!!settlementDate}
        label={settlementDate ? `Settlement date: ${settlementDate}` : 'Settlement date pending'}
      />
      <SettlementStatusRow
        id="settlementType"
        isComplete={!!settlement?.settlementType}
        label={
          settlement?.settlementType
            ? `Settlement type: ${settlementTypeLabel}`
            : 'Settlement type pending'
        }
      />
      <SettlementStatusRow
        id="settlementConfirmation"
        isComplete={confirmationComplete}
        label={
          confirmationComplete
            ? 'Settlement confirmation received'
            : 'Settlement confirmation pending'
        }
      />
    </AgxSpacer>
  );
};

export const SettlementPendingBlocker = ({
  task,
  children,
  onTasksInvalidated,
}: SettlementPendingBlockerProps): JSX.Element => {
  const blockerTypes = new Set((task.blockers ?? []).map((b) => b.blockerType));
  const hasNotMarkedAsSettledBlocker = blockerTypes.has(TaskBlockerBlockerType.NotMarkedAsSettled);
  const { data: settlement } = useStaffGetContractSettlement(
    task.contractId ?? '',
    hasNotMarkedAsSettledBlocker && !!task.contractId
  );
  const [isOpen, setIsOpen] = useState(false);

  if (!hasNotMarkedAsSettledBlocker) return <>{children}</>;

  return (
    <BlockerLayout task={task}>
      <MarkedAsSettled
        task={task}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        onTasksInvalidated={onTasksInvalidated}
      />
      <AgxEmptyState
        variant="advanced"
        label="Pending Settlement Confirmation"
        glyphType="locked"
        actionButtons={[
          {
            label: 'Mark as Settled',
            isPrimary: true,
            onClick: () => setIsOpen(true),
            order: 1,
          },
        ]}
        description={<SettlementDescription settlement={settlement} />}
      />
    </BlockerLayout>
  );
};
